import { useCallback, useEffect, useRef, useState } from 'react';
import {
  canUseSpeechToText,
  canUseTextToSpeech,
  createBrowserStt,
  createBrowserTts,
  unlockBrowserSpeech,
} from '../utils/browserSpeech';

const VOICE_REPLIES_KEY = 'aac-ask-voice-replies';
/** Flip on to show the microphone in Ask AACore AI chatboxes. */
const VOICE_INPUT_ENABLED = false;

function readVoiceReplies(): boolean {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(VOICE_REPLIES_KEY) === 'on';
}

export function useAskAiVoice(options: {
  active: boolean;
  onInterimTranscript?: (text: string) => void;
  onFinalTranscript: (text: string) => void;
}) {
  const { active, onInterimTranscript, onFinalTranscript } = options;
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [speechError, setSpeechError] = useState('');
  const [voiceReplies, setVoiceReplies] = useState(readVoiceReplies);
  const ttsRef = useRef(createBrowserTts());
  const sttRef = useRef<ReturnType<typeof createBrowserStt>>(null);
  const onInterimRef = useRef(onInterimTranscript);
  const onFinalRef = useRef(onFinalTranscript);
  onInterimRef.current = onInterimTranscript;
  onFinalRef.current = onFinalTranscript;

  const stopSpeaking = useCallback(() => {
    ttsRef.current.stop();
    setSpeaking(false);
  }, []);

  const stopListening = useCallback(() => {
    sttRef.current?.stop();
    sttRef.current = null;
    setListening(false);
  }, []);

  const speak = useCallback(async (text: string) => {
    if (!active || !voiceReplies || !canUseTextToSpeech()) return;
    setSpeaking(true);
    try {
      await ttsRef.current.speak(text);
    } finally {
      setSpeaking(ttsRef.current.speaking());
    }
  }, [active, voiceReplies]);

  const startListening = useCallback(() => {
    if (!VOICE_INPUT_ENABLED || !canUseSpeechToText()) {
      if (VOICE_INPUT_ENABLED) {
        setSpeechError('Voice input is not supported in this browser. Try Chrome or Edge.');
      }
      return;
    }
    unlockBrowserSpeech();
    stopSpeaking();
    setSpeechError('');
    sttRef.current?.stop();
    const session = createBrowserStt({
      onTranscript: (text, isFinal) => {
        if (isFinal) onFinalRef.current(text);
        else onInterimRef.current?.(text);
      },
      onError: (message) => setSpeechError(message),
      onEnd: () => {
        setListening(false);
        sttRef.current = null;
      },
    });
    sttRef.current = session;
    try {
      session?.start();
      setListening(true);
    } catch {
      setListening(false);
      setSpeechError('Could not start the microphone. Check browser permissions and try again.');
    }
  }, [stopSpeaking]);

  const toggleListening = useCallback(() => {
    if (listening) stopListening();
    else startListening();
  }, [listening, startListening, stopListening]);

  const toggleVoiceReplies = useCallback(() => {
    setVoiceReplies((current) => {
      const next = !current;
      window.sessionStorage.setItem(VOICE_REPLIES_KEY, next ? 'on' : 'off');
      if (!next) {
        ttsRef.current.stop();
        setSpeaking(false);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (active) return undefined;
    stopListening();
    stopSpeaking();
    return undefined;
  }, [active, stopListening, stopSpeaking]);

  useEffect(() => () => {
    stopListening();
    stopSpeaking();
  }, [stopListening, stopSpeaking]);

  return {
    canListen: VOICE_INPUT_ENABLED && canUseSpeechToText(),
    canSpeak: canUseTextToSpeech(),
    listening,
    speaking,
    speechError,
    voiceReplies,
    startListening,
    stopListening,
    toggleListening,
    speak,
    stopSpeaking,
    toggleVoiceReplies,
    setSpeechError,
  };
}
